'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Typo3SectionSpotSanityContentShop_Item } from '@/types/typo3/sections/spotsanity-content-shop'
import { BaseLink, Button, Chip, Title, Tooltip } from '@/ui/components'
import { generateAltText } from '@/utils'
import { MEDIA } from '@spot'
import { memo, useMemo } from 'react'
import Img from '../Img/Img'

interface CardVerticalShopProps
  extends React.HTMLAttributes<HTMLAnchorElement> {
  data: Typo3SectionSpotSanityContentShop_Item
}

const CardVerticalShop = ({
  data,
  className,
  ...props
}: CardVerticalShopProps) => {
  const D = useStoreI18n.use.dictionary()
  const currentLocale = useStoreI18n.use.currentLocale()

  const { title, images, infoPrice, priceText, facetShopCategory } = data || {}

  const subTitle = data?.location
  const image = images && images.length > 0 ? images[0] : undefined

  const imageAlt = useMemo(
    () => generateAltText('story', title, subTitle, currentLocale),
    [title, subTitle, currentLocale, image]
  )

  return (
    <BaseLink nextLink={true} {...data?.link} {...props}>
      <div
        className={
          'group bg-shop-300 text-primary-100 relative md:flex md:flex-col'
        }
      >
        <div className="relative">
          {image && (
            <Img
              data={image}
              alt={imageAlt}
              objectCover
              crop={{
                width: 442,
                height: 248
              }}
              fill
              className="aspect-video"
              hoverGroupEffect
              sizes={`${MEDIA.tablet} 45rem`}
              quality={90}
            />
          )}
          {facetShopCategory?.length && (
            <div className="gap-xs absolute bottom-0 left-0 flex">
              {facetShopCategory.map((category) => (
                <Chip key={category.facetId} variant="shop" size="shop">
                  {category.title}
                </Chip>
              ))}
            </div>
          )}
        </div>

        <div className={cn('px-md pb-lg pt-sm')}>
          <div className="gap-y-2xs flex flex-col">
            <Title className={'h4 line-clamp-3'}>{title}</Title>
            {subTitle && (
              <p className="textstyle-md-regular line-clamp-3">{subTitle}</p>
            )}
          </div>
          <div className="mt-xs flex justify-end">
            {infoPrice ? (
              <Tooltip>
                <Tooltip.Trigger>
                  <Button
                    variant="text"
                    icon="material-symbols:info"
                    iconPosition="right"
                    className="textstyle-md-bold text-primary-100"
                  >
                    {priceText}
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>{infoPrice}</Tooltip.Content>
              </Tooltip>
            ) : (
              <div className="textstyle-md-bold text-primary-100">
                {priceText}
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseLink>
  )
}

export default memo(CardVerticalShop)
